import { useState } from "react";
import TableTemplate, {
  IAdditional,
  IAdditionalHeaderProps,
} from "../../TableTemplate/tableTemplate";
import { TableCell } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import FullScreenLoading from "../../../Components/Loading/FullScreenLoading";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import PauseIcon from "@mui/icons-material/Pause";
import { useAppDispatch } from "../../../App/hooks";
import { CommonTableUI } from "../../TableTemplate/common";
import Modal from "../../../Components/Modal/Modal";
import CCOrderChancesTable from "../ChancesTable";
import Button from "../../../Components/Button";
import { OrderStatus } from "../../../Interfaces/orderInterfaces";
import {
  CCOrderTablePause,
  CCOrderTablePlay,
  deleteCCOpenOrders,
  deleteCCOrders,
  getCCOrderAggregationChart,
  getCCOrderBrokerOrderTable,
  getCCOrderDetailsOrderTable,
} from "src/Requests/ccOrder";
import { ICCOrderTableReport } from "src/Interfaces/ccOrder";
import EditIcon from "@mui/icons-material/Edit";
import { setCCOrderEditRowValues } from "src/Features/CCOrder/ccOrderSlice";
import useRequest from "src/Hooks/useRequest";
import { WrappedTimeSeriesChart } from "src/Components";
import TimelineIcon from "@mui/icons-material/Timeline";
import { CCOrderTableAdditional } from "../Order/Additional";

const CCAggregateOrderTableAdditionalHeader = ({
  selectedRowsIds,
  setSelectedRowsIds,
  onAction,
}: IAdditionalHeaderProps) => {
  const [loading, setLoading] = useState(false);

  const bulkDeleteOrderHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    deleteCCOrders(selectedRowsIds, true)
      .then((resp) => {
        onAction();
        toast.success(resp.msg);
        setSelectedRowsIds([]);
      })
      .catch(packetErrorHandler);
  };

  const bulkPauseOrderHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length) return;
    CCOrderTablePause(selectedRowsIds, true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const bulkPlayOrderHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!selectedRowsIds.length) return;
    CCOrderTablePlay(selectedRowsIds, true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const cancelAllOpenOrdersHandler = () => {
    if (!selectedRowsIds.length) return;
    setLoading(true);
    deleteCCOpenOrders(selectedRowsIds)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler)
      .finally(() => setLoading(false));
  };

  return (
    <>
      {loading ? <FullScreenLoading /> : <></>}
      <TableCell />
      <TableCell>
        <div className="flex flex-row">
          <div
            onClick={selectedRowsIds.length ? bulkPauseOrderHandler : undefined}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PauseIcon
              className={`text-sky-900 cursor-pointer  ${
                selectedRowsIds.length ? "text-sky-900 " : "text-gray-300"
              } transition-all`}
            />
          </div>
          <div
            onClick={selectedRowsIds.length ? bulkPlayOrderHandler : undefined}
            className="hover:bg-slate-200 transition-all rounded p-1"
          >
            <PlayCircleIcon
              className={`text-sky-900 cursor-pointer ${
                selectedRowsIds.length ? "text-sky-900 " : "text-gray-300"
              } transition-all`}
            />
          </div>
        </div>
      </TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>
      <TableCell></TableCell>

      <TableCell>
        <div
          onClick={selectedRowsIds.length ? cancelAllOpenOrdersHandler : undefined}
          className={`text-xs py-1 px-2 rounded ${
            selectedRowsIds.length
              ? "bg-red-700 text-white cursor-pointer"
              : "bg-white text-gray-500 border-[1px] border-gray-300 cursor-not-allowed"
          }`}
        >
          حذف سفارشات باز
        </div>
      </TableCell>

      <TableCell>
        <div
          className="hover:bg-red-100 transition-all rounded p-1"
          onClick={selectedRowsIds.length ? bulkDeleteOrderHandler : undefined}
        >
          <DeleteIcon
            fontSize="small"
            className={`cursor-pointer ${
              selectedRowsIds.length ? "text-red-900 " : "text-gray-300"
            } transition-all`}
          />
        </div>
      </TableCell>
    </>
  );
};

const CCAggregateOrderTableAdditionalCol: React.FC<{
  row: ICCOrderTableReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const [isChancesOpen, setIsChancesOpen] = useState(false);
  const [isBrokerOrderTableOpen, setIsBrokerOrderTableOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDetailsAggregationTableOpen, setIsDetailsAggregationTableOpen] =
    useState(false);
  const [isGraphModalOpen, setIsGraphModalOpen] = useState(false);
  const dispatch = useAppDispatch();

  const orderGraphRequest = useRequest(
    () => getCCOrderAggregationChart(row.id),
    packetErrorHandler,
  );

  const cancelOrderHandler = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    e.stopPropagation();
    deleteCCOrders([row.id], true)
      .then((resp) => {
        onAction();
        toast.success(resp.msg);
      })
      .catch(packetErrorHandler);
  };

  const pauseOrderHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    CCOrderTablePause([row.id], true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const playOrderHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    CCOrderTablePlay([row.id], true)
      .then((resp) => {
        if (resp.data) {
          toast.success(resp.msg);
          onAction();
        }
      })
      .catch(packetErrorHandler);
  };

  const getBrokerOrderTableUI = () => (
    <TableTemplate<{ id: number }, any>
      refreshTableTime={5000}
      getDataTable={(_, offset, pageSize) =>
        getCCOrderBrokerOrderTable({ id: row.id }, offset, pageSize)
      }
      TableUI={({ reports, onAction }) => (
        <CommonTableUI reports={reports} onAction={onAction} />
      )}
    />
  );

  const editHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setCCOrderEditRowValues({ ...row }));
  };

  const getGraphModalUI = () => {
    if (!orderGraphRequest.response) return null;
    return (
      <div className="chart-fill-modal">
        <WrappedTimeSeriesChart dataset={orderGraphRequest.response} className="h-full w-full" />
      </div>
    );
  };

  const ccOrderModalBtnClickHandler = () => {
    orderGraphRequest.fetchData();
    setIsGraphModalOpen(true);
  };

  const getDetailsAggregationTableUI = () => (
    <TableTemplate<{ id: number }, any>
      refreshTableTime={5000}
      getDataTable={(_, offset, pageSize) =>
        getCCOrderDetailsOrderTable({ conditionId: row.id }, offset, pageSize)
      }
      TableUI={({ reports, onAction }) => (
        <CommonTableUI
          reports={reports}
          onAction={onAction}
          AdditionalBody={CCOrderTableAdditional}
        />
      )}
    />
  );

  return (
    <>
      <TableCell>
        {loading ? <FullScreenLoading /> : <></>}
        <div className="w-16 mx-auto" onClick={editHandler}>
          <EditIcon className="text-sky-700 cursor-pointer" />
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-row justify-start items-center gap-2">
          <div className="hover:bg-slate-300 transition-all rounded p-1">
            <PauseIcon
              className="text-sky-700 cursor-pointer"
              onClick={pauseOrderHandler}
            />
          </div>
          <div className="hover:bg-slate-300 transition-all rounded p-1">
            <PlayCircleIcon
              className="text-sky-700 cursor-pointer"
              onClick={playOrderHandler}
            />
          </div>
        </div>
      </TableCell>
      <TableCell>
        {isChancesOpen ? (
          <Modal title="شانس‌ها"
            UI={
              <CCOrderChancesTable searchParam={{ id: row.id }} />
            }
            onClose={() => setIsChancesOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsChancesOpen(true)}
          text="شانس ها"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        {isBrokerOrderTableOpen ? (
          <Modal title="سفارشات"
            UI={getBrokerOrderTableUI()}
            onClose={() => setIsBrokerOrderTableOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsBrokerOrderTableOpen(true)}
          text="سفارشات"
          variant="contained"
          className="table-btn"
        />
      </TableCell>
      <TableCell>
        {isDetailsAggregationTableOpen ? (
          <Modal title="جزئیات"
            UI={getDetailsAggregationTableUI()}
            onClose={() => setIsDetailsAggregationTableOpen(false)}
          />
        ) : null}
        <Button
          onClick={() => setIsDetailsAggregationTableOpen(true)}
          text="جزییات"
          variant="contained"
          className="table-btn"
        />
      </TableCell>

      <TableCell>
        <>
          {isGraphModalOpen ? (
            <Modal title="نمودار"
              onClose={() => setIsGraphModalOpen(false)}
              UI={getGraphModalUI()}
              fullScreen
            />
          ) : null}
          <Button
            onClick={ccOrderModalBtnClickHandler}
            text={<TimelineIcon className="!text-base" />}
            variant="contained"
            className="table-btn"
          />
        </>
      </TableCell>

      <TableCell>
        {row.orderStatus !== OrderStatus.Cancelled ? (
          <div
            onClick={cancelOrderHandler}
            className="hover:bg-red-100 transition-all rounded p-1"
          >
            <DeleteIcon
              fontSize="small"
              className="text-red-700 cursor-pointer"
            />
          </div>
        ) : null}
      </TableCell>
    </>
  );
};

export const CCAggregateOrderTableAdditional: IAdditional<ICCOrderTableReport> =
  {
    getHeader: CCAggregateOrderTableAdditionalHeader,
    getRow: CCAggregateOrderTableAdditionalCol,
  };
